import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Plus, ArrowLeft, Trash2, MessageSquare, AlertTriangle, CheckCircle2, Lock, LayoutDashboard, ListChecks, FileStack, Search, MoreHorizontal, ChevronUp, ChevronDown, Download, CalendarClock, Sparkles, PhoneForwarded, Printer, Phone } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ProjectTemplates } from "@/components/ProjectTemplates";
import { MessageCenter } from "@/components/MessageCenter";
import { VendorTickets } from "@/components/VendorTickets";
import { LnpBoard } from "@/components/LnpBoard";
import { CollapsibleCard, useSeen } from "@/components/CollapsibleCard";

const TASK_META = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600", dot: "#94a3b8" },
  in_progress: { label: "In progress", cls: "bg-blue-50 text-blue-700", dot: "#2563eb" },
  blocked: { label: "Blocked", cls: "bg-amber-50 text-amber-700", dot: "#d97706" },
  done: { label: "Done", cls: "bg-green-50 text-green-700", dot: "#16a34a" },
};
const TASK_ORDER = ["not_started", "in_progress", "blocked", "done"];
const PROJ_STATUS = ["active", "on_hold", "completed", "cancelled"];
const STATUS_TONE = {
  active: "bg-blue-50 text-blue-700 border-blue-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200",
  completed: "bg-green-50 text-green-700 border-green-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};
const StatusTag = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${STATUS_TONE[status] || "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid="project-status-tag">
    {(status || "—").replace("_", " ")}
  </span>
);
const CompactDelta = ({ delta }) => {
  if (!delta || delta.days == null) return <span className="text-xs text-slate-300" data-testid="project-delta-cell">—</span>;
  const d = delta.diff;
  const tone = d <= 0 ? "bg-green-50 text-green-700 border-green-200" : d <= 2 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200";
  return (
    <span title={`${delta.days}d vs ${delta.target_days}d target · ${delta.is_actual ? "actual" : "planned"}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tone}`} data-testid="project-delta-cell">
      {d > 0 ? "+" : ""}{d}d
    </span>
  );
};
const TYPE_TONE = {
  UCaaS: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Hospitality: "bg-rose-50 text-rose-700 border-rose-200",
  Fax: "bg-violet-50 text-violet-700 border-violet-200",
  "UC Analog": "bg-amber-50 text-amber-700 border-amber-200",
};
const AVATAR_TONES = ["bg-emerald-600", "bg-blue-600", "bg-violet-600", "bg-rose-500", "bg-amber-600", "bg-cyan-600"];

function fmtDate(s) { return s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric" }) : "—"; }
function initials(name) { return (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase(); }
function avatarTone(name) { let h = 0; for (const c of name || "") h = (h + c.charCodeAt(0)) % AVATAR_TONES.length; return AVATAR_TONES[h]; }

const ScoreRing = ({ pct }) => {
  const v = pct == null ? null : pct;
  const r = 16, c = 2 * Math.PI * r, off = c * (1 - (v || 0) / 100);
  const color = v == null ? "#cbd5e1" : v >= 66 ? "#16a34a" : v >= 33 ? "#2563eb" : "#dc2626";
  return (
    <div className="relative flex h-10 w-10 items-center justify-center">
      <svg width="40" height="40" className="-rotate-90">
        <circle cx="20" cy="20" r={r} fill="none" stroke="#eef2f7" strokeWidth="4" />
        {v != null && <circle cx="20" cy="20" r={r} fill="none" stroke={color} strokeWidth="4" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" />}
      </svg>
      <span className="absolute text-[11px] font-semibold" style={{ color: v == null ? "#94a3b8" : color }}>{v == null ? "n/a" : v}</span>
    </div>
  );
};

const OwnerAvatar = ({ name }) => name ? (
  <span title={name} className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold text-white ${avatarTone(name)}`}>{initials(name)}</span>
) : <span className="text-xs text-slate-300">—</span>;

const TypeTag = ({ type }) => type ? (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${TYPE_TONE[type] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />{type}
  </span>
) : null;

function DeltaBadge({ delta }) {
  if (!delta || delta.days == null) return <span className="text-xs text-slate-400" data-testid="delta-none">Dates not set</span>;
  const d = delta.diff;
  const tone = d <= 0 ? "bg-green-50 text-green-700 border-green-200" : d <= 2 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${tone}`} data-testid="delta-badge">
      {delta.days}d vs {delta.target_days}d target ({d > 0 ? "+" : ""}{d})<span className="font-normal opacity-70">· {delta.is_actual ? "actual" : "planned"}</span>
    </span>
  );
}

export default function Projects() {
  const { user, has } = useAuth();
  const isStaff = user?.base_role === "staff";
  const canCreate = has("projects.create");
  const canManage = has("projects.edit");
  const [view, setView] = useState("dashboard");
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [npInitial, setNpInitial] = useState(null);

  const runDraft = async () => {
    if (!aiText.trim()) { toast.error("Describe the project first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/assistant/draft-project", { description: aiText.trim() });
      const d = data.draft;
      setNpInitial({ name: d.name, product_type: d.product_type, target_go_live: d.target_go_live || "" });
      setAiOpen(false); setAiText(""); setCreateOpen(true);
      toast.success("Draft ready — pick a template & partner, then create");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not draft a project"); }
    finally { setAiLoading(false); }
  };

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  useEffect(() => { if (!user?.user_id) return; loadProjects(); api.get("/messages/unread-count").then((r) => setMsgCount(r.data.count)).catch(() => {}); }, [user]);
  useEffect(() => { if (canCreate && new URLSearchParams(window.location.search).get("ai") === "1") setAiOpen(true); }, [canCreate]);
  const openProject = (pid) => api.get(`/projects/${pid}`).then((r) => setSelected(r.data)).catch(() => toast.error("Failed to open project"));

  const NAV = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, show: true },
    { key: "templates", label: "Templates", icon: FileStack, show: isStaff },
    { key: "tasks", label: "All Tasks", icon: ListChecks, show: true },
    { key: "messages", label: "Messages", icon: MessageSquare, show: true, badge: msgCount },
  ].filter((n) => n.show);

  return (
    <div className="flex min-h-[calc(100vh-160px)] gap-6" data-testid="projects-page">
      {/* Sub-nav */}
      <aside className="hidden w-48 shrink-0 md:block" data-testid="projects-subnav">
        <div className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Projects</div>
        <nav className="space-y-1">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`projnav-${n.key}`} onClick={() => { setSelected(null); setView(n.key); }}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${view === n.key && !selected ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
              {n.badge > 0 && <span className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{n.badge}</span>}
            </button>
          ))}
        </nav>
      </aside>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {selected ? (
          <ProjectDetail detail={selected} setDetail={setSelected} onBack={() => { setSelected(null); loadProjects(); }} canManage={canManage} />
        ) : view === "dashboard" ? (
          <DashboardView projects={projects} onOpen={openProject} canCreate={canCreate} onNew={() => { setNpInitial(null); setCreateOpen(true); }} onAiNew={() => setAiOpen(true)} onChanged={loadProjects} />
        ) : view === "templates" ? (
          <div>
            <div className="mb-4"><h1 className="text-xl font-semibold text-slate-800">Templates</h1><p className="text-sm text-slate-400">Product-type playbooks of steps, tasks and dependencies.</p></div>
            <ProjectTemplates canManage={canManage} />
          </div>
        ) : view === "tasks" ? (
          <AllTasksView onOpen={openProject} />
        ) : (
          <MessageCenter onCountChange={setMsgCount} />
        )}
      </div>

      {createOpen && <NewProjectDialog initial={npInitial} onClose={() => setCreateOpen(false)} onCreated={(d) => { setCreateOpen(false); setSelected(d); loadProjects(); }} />}

      <Dialog open={aiOpen} onOpenChange={(o) => { if (!o) { setAiOpen(false); setAiText(""); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-violet-600" /> Draft a project with AI</DialogTitle>
            <DialogDescription>Describe the engagement in plain English. Claude drafts a name and picks the product type (UCaaS / Hospitality); you then choose the template & partner and create it.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="proj-ai-input" rows={3} autoFocus value={aiText} onChange={(e) => setAiText(e.target.value)}
            placeholder="e.g. New UCaaS rollout for Sandman Hotels, 120 rooms, target go-live end of August" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAiOpen(false); setAiText(""); }} disabled={aiLoading}>Cancel</Button>
            <Button data-testid="proj-ai-submit" onClick={runDraft} disabled={aiLoading} className="bg-violet-600 hover:bg-violet-700 text-white">{aiLoading ? "Drafting…" : <><Sparkles className="h-4 w-4" /> Draft project</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const LnpChip = ({ s }) => {
  if (!s || (!s.open && !s.overdue)) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700" data-testid="lnp-chip">
      <PhoneForwarded className="h-3 w-3" /> Ports: {s.open} open{s.overdue > 0 ? <span className="text-rose-600"> · {s.overdue} overdue</span> : null}
    </span>
  );
};

const SectionPlaceholder = ({ title, icon: Icon, note, projectId }) => {
  const slug = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <CollapsibleCard testid={`section-${slug}`} icon={Icon} title={title} defaultOpen={false} persistKey={`proj-${projectId}-section-${slug}`}
      badge={<span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">Coming soon</span>}>
      <p className="px-5 py-4 text-sm text-slate-400">{note}</p>
    </CollapsibleCard>
  );
};

const DashboardView = ({ projects, onOpen, canCreate, onNew, onAiNew, onChanged }) => {
  const { has } = useAuth();
  const navigate = useNavigate();
  const canManage = has("projects.edit");
  const canReport = has("reports.view");
  const canEdit = has("projects.edit");
  const [q, setQ] = usePersistedState("projects.q", "");
  const [status, setStatus] = usePersistedState("projects.status", "active");
  const [sort, setSort] = usePersistedState("projects.sort", { key: "activity", dir: "desc" });
  const [techStaff, setTechStaff] = useState([]);
  const [lnpSummary, setLnpSummary] = useState({});

  useEffect(() => { if (canEdit) api.get("/staff/tech").then((r) => setTechStaff(r.data)).catch(() => {}); }, [canEdit]);
  useEffect(() => { api.get("/lnp/summary").then((r) => setLnpSummary(r.data || {})).catch(() => {}); }, []);

  const assignLeadTech = async (p, uid) => {
    try {
      await api.put(`/projects/${p.project_id}`, { lead_tech_user_id: uid === "none" ? "" : uid });
      toast.success("Lead Tech updated");
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    const t = toast.loading("Preparing report…");
    try { await downloadFromUrl("/projects-summary/pdf", `Projects-Report-${new Date().toISOString().slice(0, 10)}.pdf`); toast.success("Report downloaded", { id: t }); }
    catch { toast.error("Report download failed", { id: t }); }
  };

  const rows = useMemo(() => {
    let r = projects.filter((p) => (status === "all" || p.status === status));
    if (q.trim()) { const s = q.toLowerCase(); r = r.filter((p) => p.name.toLowerCase().includes(s) || (p.customer_name || "").toLowerCase().includes(s) || (p.partner_name || "").toLowerCase().includes(s)); }
    const getv = (p) => ({ name: p.name?.toLowerCase(), score: p.progress?.pct || 0, tasks: p.progress?.done || 0, start: p.start_date || "", end: p.actual_go_live || p.target_go_live || "", activity: p.activity || "" }[sort.key]);
    r = [...r].sort((a, b) => { const x = getv(a), y = getv(b); return (x < y ? -1 : x > y ? 1 : 0) * (sort.dir === "asc" ? 1 : -1); });
    return r;
  }, [projects, q, status, sort]);

  const Th = ({ k, children, className }) => (
    <th className={`px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${k ? "cursor-pointer select-none" : ""} ${className || ""}`}
      onClick={() => k && setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }))}>
      <span className="inline-flex items-center gap-1">{children}{k && sort.key === k && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}</span>
    </th>
  );

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div><h1 className="text-xl font-semibold text-slate-800">Projects</h1><p className="text-sm text-slate-400">Manage your onboarding projects and track their progress.</p></div>
        <div className="flex items-center gap-2">
          {canManage && <Button variant="outline" data-testid="export-report-button" onClick={downloadPdf}><Download className="h-4 w-4" /> Export PDF</Button>}
          {canManage && canReport && <Button variant="outline" size="icon" data-testid="report-settings-button" title="Schedule or email this report (Reports hub)" onClick={() => navigate("/reports/projects")}><CalendarClock className="h-4 w-4" /></Button>}
          {canCreate && <Button variant="outline" data-testid="proj-ai-draft-button" onClick={onAiNew} className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"><Sparkles className="h-4 w-4" /> AI draft</Button>}
          {canCreate && <Button data-testid="new-project-button" onClick={onNew} className="bg-primary hover:bg-primary/90 text-primary-foreground"><Plus className="h-4 w-4" /> New project</Button>}
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="projects-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects..." className="pl-9" />
        </div>
        <div className="flex items-center gap-1.5" data-testid="status-filters">
          {["all", ...PROJ_STATUS].map((s) => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${status === s ? "border-primary bg-primary/10 text-primary" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
              {s.replace("_", " ")}{status === s && s !== "all" ? " ✕" : ""}
            </button>
          ))}
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="border-b border-slate-100 bg-slate-50/60">
              <tr>
                <Th k="name">Name</Th><Th>Client</Th><Th k="score">Score</Th><Th>Owner</Th><Th>Lead Tech</Th><Th k="tasks">Tasks</Th>
                <Th k="start">Start date</Th><Th k="end">Go-live</Th><Th>Delta</Th><Th>Status</Th><Th k="activity">Activity</Th><Th>Tags</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={13} className="px-3 py-12 text-center text-sm text-slate-400" data-testid="projects-empty">No projects match.</td></tr>
              ) : rows.map((p) => (
                <tr key={p.project_id} data-testid={`project-row-${p.project_id}`} onClick={() => onOpen(p.project_id)}
                  className="cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50/70">
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"><FolderKanban className="h-4 w-4" /></span>
                      <span className="font-medium text-slate-800">{p.name}</span>
                    </div>
                    {lnpSummary[p.project_id] && <div className="mt-1 pl-9"><LnpChip s={lnpSummary[p.project_id]} /></div>}
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-500">{p.customer_name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-3"><ScoreRing pct={p.progress?.total ? p.progress.pct : null} /></td>
                  <td className="px-3 py-3"><OwnerAvatar name={p.owner_name} /></td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    {canEdit ? (
                      <Select value={p.lead_tech_user_id || "none"} onValueChange={(v) => assignLeadTech(p, v)}>
                        <SelectTrigger className="h-8 w-40" data-testid={`lead-tech-select-${p.project_id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unassigned</SelectItem>
                          {techStaff.map((t) => <SelectItem key={t.user_id} value={t.user_id}>{t.name} · {t.team}{t.qualification ? ` · ${t.qualification}` : ""}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-sm text-slate-600" data-testid={`lead-tech-name-${p.project_id}`}>{p.lead_tech_name || <span className="text-slate-300">—</span>}</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="w-28"><Progress value={p.progress?.pct || 0} className="h-1.5" /><div className="mt-1 text-right text-[11px] text-slate-400">{p.progress?.done} / {p.progress?.total}</div></div>
                  </td>
                  <td className="px-3 py-3 text-sm text-slate-500">{fmtDate(p.start_date)}</td>
                  <td className="px-3 py-3 text-sm text-slate-500">{fmtDate(p.actual_go_live || p.target_go_live)}</td>
                  <td className="px-3 py-3"><CompactDelta delta={p.delta} /></td>
                  <td className="px-3 py-3"><StatusTag status={p.status} /></td>
                  <td className="px-3 py-3 text-sm text-slate-500">{fmtDate(p.activity)}</td>
                  <td className="px-3 py-3"><TypeTag type={p.product_type} /></td>
                  <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`project-actions-${p.project_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end"><DropdownMenuItem onClick={() => onOpen(p.project_id)}>Open</DropdownMenuItem></DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

const AllTasksView = ({ onOpen }) => {
  const [tasks, setTasks] = useState([]);
  useEffect(() => { api.get("/project-tasks").then((r) => setTasks(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <div className="mb-4"><h1 className="text-xl font-semibold text-slate-800">All Tasks</h1><p className="text-sm text-slate-400">Every task across your projects.</p></div>
      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-slate-100 bg-slate-50/60 text-[11px] uppercase tracking-wide text-slate-400">
            <tr><th className="px-4 py-2.5 text-left">Task</th><th className="px-4 py-2.5 text-left">Project</th><th className="px-4 py-2.5 text-left">Owner</th><th className="px-4 py-2.5 text-left">Status</th></tr>
          </thead>
          <tbody>
            {tasks.length === 0 ? <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-slate-400">No tasks.</td></tr> :
              tasks.map((t) => (
                <tr key={t.task_id} data-testid={`alltask-${t.task_id}`} className="cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50/70" onClick={() => onOpen(t.project_id)}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">{t.name}
                      {t.blocked_by?.length > 0 && <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"><AlertTriangle className="h-3 w-3" /> waiting</span>}
                    </div>
                    <div className="text-xs text-slate-400">{t.step}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{t.project_name}</td>
                  <td className="px-4 py-2.5 text-sm text-slate-500">{t.assignee_name || t.owner_label}</td>
                  <td className="px-4 py-2.5"><span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${TASK_META[t.status].cls}`}><span className="h-2 w-2 rounded-full" style={{ backgroundColor: TASK_META[t.status].dot }} />{TASK_META[t.status].label}</span></td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const NewProjectDialog = ({ onClose, onCreated, initial }) => {
  const [templates, setTemplates] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ name: "", product_type: "", template_id: "", partner_org_id: "", customer_org_id: "", start_date: "", ...(initial || {}) });
  useEffect(() => {
    api.get("/project-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/organizations").then((r) => setOrgs(r.data)).catch(() => {});
  }, []);
  const types = [...new Set(templates.map((t) => t.product_type))];
  const typeTemplates = templates.filter((t) => t.product_type === form.product_type);
  const partners = orgs.filter((o) => o.type === "partner");
  const customers = orgs.filter((o) => o.type === "customer");
  const submit = async () => {
    if (!form.name || !form.template_id || !form.partner_org_id) { toast.error("Name, template and partner are required"); return; }
    try {
      const { data } = await api.post("/projects", { ...form, customer_org_id: form.customer_org_id || null, start_date: form.start_date || null });
      toast.success("Project created"); onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New project</DialogTitle><DialogDescription>Spin up a project from a product-type template.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Project name</Label><Input data-testid="np-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme UCaaS Rollout" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Product type</Label>
              <Select value={form.product_type} onValueChange={(v) => setForm({ ...form, product_type: v, template_id: "" })}>
                <SelectTrigger data-testid="np-product-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Template</Label>
              <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })} disabled={!form.product_type}>
                <SelectTrigger data-testid="np-template"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{typeTemplates.map((t) => <SelectItem key={t.template_id} value={t.template_id}>{t.name} ({t.target_days}d)</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Partner</Label>
              <Select value={form.partner_org_id} onValueChange={(v) => setForm({ ...form, partner_org_id: v })}>
                <SelectTrigger data-testid="np-partner"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{partners.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Customer (optional)</Label>
              <Select value={form.customer_org_id || "none"} onValueChange={(v) => setForm({ ...form, customer_org_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="np-customer"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{customers.map((o) => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Start date</Label><Input type="date" data-testid="np-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button data-testid="np-create" onClick={submit} className="bg-primary hover:bg-primary/90 text-primary-foreground">Create project</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProjectDetail = ({ detail, setDetail, onBack, canManage }) => {
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(true);
  const isStaff = detail.base_role === "staff";
  const isCustomer = detail.base_role === "customer";
  const isPartner = detail.base_role === "partner";
  const canEditTask = (t) => isStaff || (isPartner && t.owner_side === "partner");
  const [lnpStat, setLnpStat] = useState(null);
  const refreshLnp = () => { if (isCustomer) return; api.get("/lnp/summary").then((r) => setLnpStat((r.data || {})[detail.project_id] || null)).catch(() => {}); };
  useEffect(() => { refreshLnp(); }, [detail.project_id, isCustomer]); // eslint-disable-line
  const latestComment = (detail.comments || []).reduce((m, c) => (c.created_at > m ? c.created_at : m), "");
  const commSeen = useSeen(`proj-${detail.project_id}-communication`, latestComment);

  const setTaskStatus = async (t, status) => {
    try { const { data } = await api.put(`/projects/${detail.project_id}/tasks/${t.task_id}`, { status }); setDetail(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const setDates = async (patch) => {
    try { const { data } = await api.put(`/projects/${detail.project_id}`, patch); setDetail(data); toast.success("Saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const postComment = async () => {
    if (!comment.trim()) return;
    try { const { data } = await api.post(`/projects/${detail.project_id}/comments`, { body: comment, internal }); setDetail(data); setComment(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try { await api.delete(`/projects/${detail.project_id}`); toast.success("Project deleted"); onBack(); }
    catch (e) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-5" data-testid="project-detail">
      <Button variant="ghost" size="sm" data-testid="project-back" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Back to projects</Button>

      <Card className="border-slate-200 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2"><FolderKanban className="h-5 w-5 text-primary" /><h1 className="text-xl font-semibold text-slate-800">{detail.name}</h1></div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <p className="text-sm text-slate-500">{detail.product_type} · {detail.partner_name}{detail.customer_name ? ` → ${detail.customer_name}` : ""}</p>
              {!isCustomer && <LnpChip s={lnpStat} />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isStaff && (
              <Select value={detail.status} onValueChange={(v) => setDates({ status: v })}>
                <SelectTrigger className="h-9 w-[140px]" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJ_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {canManage && <Button variant="outline" size="icon" data-testid="project-delete" onClick={del}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <DateField label="Start date" value={detail.start_date} editable={isStaff} onChange={(v) => setDates({ start_date: v })} testid="proj-start" />
          <DateField label="Target go-live" value={detail.target_go_live} editable={isStaff} onChange={(v) => setDates({ target_go_live: v })} testid="proj-target" />
          <DateField label="Actual go-live" value={detail.actual_go_live} editable={isStaff} onChange={(v) => setDates({ actual_go_live: v })} testid="proj-actual" />
          <div><div className="text-xs font-medium uppercase tracking-wide text-slate-400">Delta</div><div className="mt-1.5"><DeltaBadge delta={detail.delta} /></div></div>
        </div>
        <div className="mt-4 flex items-center gap-3"><Progress value={detail.progress?.pct || 0} className="h-2 flex-1" /><span className="text-sm font-medium text-slate-500">{detail.progress?.done}/{detail.progress?.total} done</span></div>
      </Card>

      <div className="space-y-4">
        {detail.steps.map((step) => {
          const total = step.tasks.length;
          const done = step.tasks.filter((t) => t.status === "done").length;
          const allDone = total > 0 && done === total;
          const pct = total ? Math.round((done / total) * 100) : 0;
          return (
          <CollapsibleCard
            key={step.name}
            testid={`step-${step.name.toLowerCase().replace(/\s+/g, "-")}`}
            title={step.name}
            dot={total === 0 ? "slate" : allDone ? "green" : done > 0 ? "yellow" : "red"}
            persistKey={`proj-${detail.project_id}-step-${step.name}`}
            count={total}
            countTone={allDone ? "green" : "primary"}
            defaultOpen={false}
            headerInfo={total > 0 ? (
              <div className="flex items-center gap-2" data-testid={`step-progress-${step.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="hidden w-24 sm:block"><Progress value={pct} className={`h-1.5 ${allDone ? "[&>div]:bg-green-500" : ""}`} /></div>
                <span className={`text-xs font-medium ${allDone ? "text-green-600" : "text-slate-500"}`}>{done} of {total} complete</span>
              </div>
            ) : null}
          >
            <div className="divide-y divide-slate-50">
              {step.tasks.map((t) => (
                <div key={t.task_id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`task-${t.task_id}`}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: TASK_META[t.status].dot }} />
                  <div className="min-w-[180px] flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium text-slate-800">{t.name}
                      {t.blocked_by?.length > 0 && <span title={`Waiting on: ${t.blocked_by.join(", ")}`} className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`blocked-${t.task_id}`}><AlertTriangle className="h-3 w-3" /> waiting</span>}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                      <span className="rounded bg-slate-100 px-1.5 py-0.5 capitalize">{t.owner_label}</span>
                      {t.assignee_name && <span>· {t.assignee_name}</span>}
                      {t.visibility === "shared" && <span className="text-green-600">· shared</span>}
                    </div>
                  </div>
                  {canEditTask(t) ? (
                    <Select value={t.status} onValueChange={(v) => setTaskStatus(t, v)}>
                      <SelectTrigger className="h-9 w-[150px]" data-testid={`task-status-${t.task_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{TASK_ORDER.map((s) => <SelectItem key={s} value={s}><span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: TASK_META[s].dot }} />{TASK_META[s].label}</span></SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${TASK_META[t.status].cls}`}>{t.status === "done" ? <CheckCircle2 className="h-3 w-3" /> : <Lock className="h-3 w-3 opacity-50" />}{TASK_META[t.status].label}</span>
                  )}
                </div>
              ))}
            </div>
          </CollapsibleCard>
          );
        })}
      </div>

      <CollapsibleCard testid="project-communication" icon={MessageSquare} title="Communication" persistKey={`proj-${detail.project_id}-communication`} defaultOpen={false} count={detail.comments.length}
        dot={commSeen.unread ? "red" : (detail.comments.length ? "green" : "slate")} dotTitle={commSeen.unread ? "New messages" : "Up to date"}
        onOpenChange={(open) => { if (open) commSeen.markSeen(); }}>
        <div className="max-h-72 space-y-3 overflow-y-auto px-5 py-4" data-testid="project-comments">
          {detail.comments.length === 0 ? <p className="text-sm text-slate-400">No messages yet.</p> : detail.comments.map((c) => (
            <div key={c.comment_id} className="rounded-lg bg-slate-50 px-3 py-2" data-testid={`comment-${c.comment_id}`}>
              <div className="flex items-center gap-2 text-xs text-slate-400"><span className="font-medium text-slate-600">{c.author_name}</span>{c.internal && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">Internal</span>}<span>· {new Date(c.created_at).toLocaleString()}</span></div>
              <p className="mt-1 text-sm text-slate-700">{c.body}</p>
            </div>
          ))}
        </div>
        {!isCustomer && (
          <div className="border-t border-slate-100 px-5 py-3">
            <Textarea data-testid="comment-input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a message…" />
            <div className="mt-2 flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-slate-500"><Switch data-testid="comment-internal" checked={internal} onCheckedChange={setInternal} />Internal {internal ? "(hidden from customer)" : "(visible to customer)"}</label>
              <Button size="sm" data-testid="comment-send" onClick={postComment} className="bg-primary hover:bg-primary/90 text-primary-foreground">Send</Button>
            </div>
          </div>
        )}
      </CollapsibleCard>

      {isStaff && <VendorTickets projectId={detail.project_id} />}

      {!isCustomer && <LnpBoard projectId={detail.project_id} canEdit={isStaff} section onChanged={refreshLnp} />}
      {isStaff && <SectionPlaceholder title="Fax" icon={Printer} projectId={detail.project_id} note="Fax porting & provisioning tracking for this project — details to follow." />}
      {isStaff && <SectionPlaceholder title="UC Analog" icon={Phone} projectId={detail.project_id} note="UC Analog line tracking for this project — details to follow." />}
    </div>
  );
};

const DateField = ({ label, value, editable, onChange, testid }) => (
  <div>
    <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
    {editable ? <Input type="date" data-testid={testid} value={value ? value.slice(0, 10) : ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9" />
      : <div className="mt-1.5 text-sm font-medium text-slate-700">{value ? new Date(value).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—"}</div>}
  </div>
);
